import React, { useState } from 'react';
import {
  Typography,
  Form,
  Input,
  Checkbox,
  Button,
  message,
  Segmented
} from 'antd';
import {
  ClockCircleOutlined,
  LockOutlined,
  MailOutlined,
  PhoneOutlined,
  SafetyCertificateOutlined,
  TeamOutlined,
  WhatsAppOutlined
} from '@ant-design/icons';
import { signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { auth } from '../firebaseConfig';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { useLanguage } from './LanguageContext';
import { getFirestore, collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import {
  firestoreTruthyDisabled,
  resolveAssistantDoctorAssignment,
} from '../utils/resolveAssistantDoctorAssignment';
import { setViewerOnlySession } from '../utils/viewerOnly';
import '../styles/Login.css';

const { Title, Text } = Typography;
const CONTACT_PHONE = '+201063030622';
const CONTACT_TEL_URL = `tel:${CONTACT_PHONE}`;
const CONTACT_WHATSAPP_URL = 'https://wa.me/201063030622';

const loginCopy = {
  en: {
    brand: 'Dr Waiting',
    eyebrow: 'Clinic command center',
    headline: 'Step into a calmer clinic day.',
    subtitle: 'Secure access for doctors and assistants to manage queues, visits, revenue, and patient flow from one polished workspace.',
    title: 'Welcome back',
    description: 'Sign in to continue to your dashboard.',
    email: 'Email address',
    emailRequired: 'Please input your email!',
    password: 'Password',
    passwordRequired: 'Please input your password!',
    remember: 'Remember me',
    submit: 'Login',
    noAccount: "Don't have an account?",
    register: 'Register',
    contact: 'Contact us',
    call: 'Call',
    whatsapp: 'WhatsApp',
    success: 'Login Successful'
  },
  ar: {
    brand: 'Dr Waiting',
    eyebrow: 'مركز إدارة العيادة',
    headline: 'ادخل إلى يوم عيادة أكثر هدوءا.',
    subtitle: 'دخول آمن للأطباء والمساعدين لإدارة الدور والزيارات والإيرادات وحركة المرضى من مساحة عمل واحدة.',
    title: 'مرحبا بعودتك',
    description: 'سجل الدخول للمتابعة إلى لوحة التحكم.',
    email: 'البريد الإلكتروني',
    emailRequired: 'برجاء إدخال البريد الإلكتروني',
    password: 'كلمة المرور',
    passwordRequired: 'برجاء إدخال كلمة المرور',
    remember: 'تذكرني',
    submit: 'تسجيل الدخول',
    noAccount: 'ليس لديك حساب؟',
    register: 'إنشاء حساب',
    contact: 'تواصل معنا',
    call: 'اتصال',
    whatsapp: 'واتساب',
    success: 'تم تسجيل الدخول بنجاح'
  }
};

interface LoginFormValues {
  email: string;
  password: string;
  remember?: boolean;
}

interface LocationState {
  from?: {
    pathname?: string;
  };
}

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as LocationState;
  const { login, logout } = useAuth();
  const { language, setLanguage, isRTL } = useLanguage();
  const copy = loginCopy[language];
  const [loading, setLoading] = useState<boolean>(false);

  const onFinish = async (values: LoginFormValues) => {
    setLoading(true);
    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        values.email,
        values.password
      );

      // Extract username from email, stored only after all checks pass below.
      const extractedUsername = values.email.split('@')[0];

      // The Firebase UID is the assistant_id, not the doctor_id.
      const assistantId = userCredential.user.uid.trim();
      console.log('Firebase UID (assistant_id):', assistantId);

      const db = getFirestore();
      const doctorClinicAssistantRef = collection(db, 'doctor_clinic_assistant');
      const assistantQuery = query(doctorClinicAssistantRef, where('assistant_id', '==', assistantId));
      const assistantSnapshot = await getDocs(assistantQuery);

      let assistantSnapshotWithSpace = null;
      if (assistantSnapshot.empty) {
        const assistantQueryWithSpace = query(doctorClinicAssistantRef, where('assistant_id', '==', assistantId + ' '));
        assistantSnapshotWithSpace = await getDocs(assistantQueryWithSpace);
      }

      console.log('Query results count:', assistantSnapshot.size);
      if (assistantSnapshotWithSpace) {
        console.log('Query results count (with space):', assistantSnapshotWithSpace.size);
      }

      let doctorId: string | null = null;
      let clinicId: string | null = null;
      const preferredClinicId = localStorage.getItem('clinicId');
      const finalSnapshot = !assistantSnapshot.empty ? assistantSnapshot : assistantSnapshotWithSpace;

      if (finalSnapshot && !finalSnapshot.empty) {
        const resolved = await resolveAssistantDoctorAssignment(
          db,
          finalSnapshot.docs,
          assistantId,
          preferredClinicId
        );
        if (resolved) {
          doctorId = resolved.doctorId;
          clinicId = resolved.clinicId;
        }
      } else {
        console.log('Trying to get document by ID:', assistantId);
        const assistantDocRef = doc(db, 'doctor_clinic_assistant', assistantId);
        const assistantDocSnap = await getDoc(assistantDocRef);

        if (assistantDocSnap.exists()) {
          const resolved = await resolveAssistantDoctorAssignment(
            db,
            [assistantDocSnap],
            assistantId,
            preferredClinicId
          );
          if (resolved) {
            doctorId = resolved.doctorId;
            clinicId = resolved.clinicId;
          }
        } else {
          console.error('No assistant document found with ID:', assistantId);
          const allDocs = await getDocs(doctorClinicAssistantRef);
          console.log('All documents in doctor_clinic_assistant collection:');
          allDocs.forEach((docSnap) => {
            console.log('Document ID:', docSnap.id, 'Data:', docSnap.data());
          });
        }
      }

      const rejectSession = async (errMsg: string) => {
        await signOut(auth).catch(() => {});
        logout();
        message.error(errMsg);
      };

      const assistantProfileSnap = await getDoc(doc(db, 'assistants', assistantId));
      if (
        assistantProfileSnap.exists() &&
        firestoreTruthyDisabled(assistantProfileSnap.data()?.disabled)
      ) {
        await rejectSession(
          'Your account has been disabled by the clinic owner. Please contact your administrator.'
        );
        return;
      }

      if (doctorId) {
        const doctorSnap = await getDoc(doc(db, 'doctors', doctorId));
        if (doctorSnap.exists() && firestoreTruthyDisabled(doctorSnap.data()?.disabled)) {
          await rejectSession(
            'Your account has been disabled by the clinic owner. Please contact your administrator.'
          );
          return;
        }

        const isOwner = doctorSnap.exists() && doctorSnap.data()?.is_owner === true;
        setViewerOnlySession(doctorSnap.exists() && doctorSnap.data()?.privileges?.viewerOnly === true);
        if (!isOwner && clinicId) {
          const dcaMemberSnap = await getDocs(
            query(
              collection(db, 'doctor_clinic_assistant'),
              where('clinic_id', '==', clinicId),
              where('doctor_id', '==', doctorId)
            )
          );
          if (dcaMemberSnap.empty) {
            await rejectSession(
              'Your account has been removed from this clinic. Please contact your administrator.'
            );
            return;
          }
        }
      } else {
        console.error('doctor_id not found in assistant document');
        await rejectSession('Doctor ID not found. Please contact administrator.');
        return;
      }

      login(extractedUsername);
      localStorage.setItem('assistantId', assistantId);
      localStorage.setItem('doctorId', doctorId);
      console.log('Successfully stored doctor_id:', doctorId);

      if (clinicId) {
        localStorage.setItem('clinicId', clinicId);
        console.log('Stored clinic_id:', clinicId);
      }

      window.dispatchEvent(new Event('doctorIdChanged'));
      message.success(copy.success);

      const from = state?.from?.pathname || '/dashboard';
      navigate(from, { replace: true });
    } catch (error: any) {
      console.error('Login error:', error);
      message.error(error.message || 'Login Failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="auth-page">
      <div className="auth-shell">
        <section className="auth-showcase" aria-label={copy.brand}>
          <div className="auth-brand">
            <span className="auth-brand-mark">
              <img src="/dr-waiting-logo.webp" alt="Dr Waiting logo" />
            </span>
            <span className="auth-brand-name">{copy.brand}</span>
          </div>
          <div className="auth-eyebrow">
            <SafetyCertificateOutlined />
            <span>{copy.eyebrow}</span>
          </div>
          <Title level={1}>{copy.headline}</Title>
          <p>{copy.subtitle}</p>
        </section>

        <section className="auth-card" aria-label={copy.title}>
          <div className="auth-card-inner">
            <div className="auth-card-header">
              <div className="auth-card-title">
                <Title level={2}>{copy.title}</Title>
                <Text>{copy.description}</Text>
              </div>
              <div className="auth-language-toggle">
                <Segmented
                  block
                  value={language}
                  onChange={(value) => setLanguage(value as 'en' | 'ar')}
                  options={[
                    { label: 'عربي', value: 'ar' },
                    { label: 'EN', value: 'en' }
                  ]}
                />
              </div>
            </div>

            <Form
              name="login"
              initialValues={{ remember: true }}
              onFinish={onFinish}
              layout="vertical"
              className="auth-form"
              dir={isRTL ? 'rtl' : 'ltr'}
            >
              <Form.Item
                name="email"
                label={copy.email}
                rules={[{
                  required: true,
                  message: copy.emailRequired,
                  type: 'email'
                }]}
              >
                <Input size="large" placeholder={copy.email} prefix={<MailOutlined />} />
              </Form.Item>

              <Form.Item
                name="password"
                label={copy.password}
                rules={[{
                  required: true,
                  message: copy.passwordRequired
                }]}
              >
                <Input.Password size="large" placeholder={copy.password} prefix={<LockOutlined />} />
              </Form.Item>

              <div className="auth-actions-row">
                <Form.Item name="remember" valuePropName="checked" noStyle>
                  <Checkbox>{copy.remember}</Checkbox>
                </Form.Item>
              </div>

              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  block
                  size="large"
                  loading={loading}
                  className="auth-submit"
                  icon={<ClockCircleOutlined />}
                >
                  {copy.submit}
                </Button>
              </Form.Item>
            </Form>

            <div className="auth-alt-link">
              <Text>
                {copy.noAccount} <RouterLink to="/register"><TeamOutlined /> {copy.register}</RouterLink>
              </Text>
            </div>

            <div className="auth-contact">
              <span>{copy.contact}</span>
              <div className="auth-contact-actions">
                <a href={CONTACT_WHATSAPP_URL} target="_blank" rel="noreferrer">
                  <WhatsAppOutlined /> {copy.whatsapp}
                </a>
                <a href={CONTACT_TEL_URL}>
                  <PhoneOutlined /> {copy.call} {CONTACT_PHONE}
                </a>
              </div>
            </div>
          </div>
        </section>
      </div>
    </main>
  );
};

export default LoginPage;
